// ---------------------------------------------------------------------------
// KgProvider — React context for the kg lifecycle (Starter).
//
// Starter runs the Cogo Schema runtime for generated apps. It consumes the
// same `@cogo/kg-repository` surface as Studio per
// `docs/agentic/architecture/kg-repository-common.md` §3.1 — no app-local
// Repository re-implementation, ontology details stay insulated.
//
// Starter-specific traits:
//   - Same-origin KG shards (`apps/cogo-starter/public/kg/` is the CDN
//     origin) → no CORS cascade. Manifest URL defaults to `/kg/manifest.json`.
//   - LCP-sensitive. Non-blocking init (plan doc §2.3 Option B): `repo.ready()`
//     runs in background; shell paints without waiting.
//   - Default flag OFF on all prod rollouts. This provider is a near-zero-cost
//     wrapper when `VITE_KG_BROWSER` is unset.
// ---------------------------------------------------------------------------

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { InitResult } from '@cogo/kg-browser'
import {
  HybridRepository,
  LocalWasmRepository,
  MapBackedRuleRegistry,
  McpGatewayFallback,
  PlatformRestFallback,
} from '@cogo/kg-repository'
import { supabase } from '../lib/supabase'
import { STARTER_SHACL_RULES } from '../validation/shacl-rules'
import { getManifestUrl, getWorkerBaseUrl, isKgBrowserEnabled } from './kg-client'

const MCP_GATEWAY_URL =
  (import.meta.env.VITE_KG_MCP_GATEWAY as string | undefined) ??
  'https://cjvgmyotqxfpxpvmwxfv.supabase.co/functions/v1/cogo-mcp-gateway'

const PLATFORM_BASE =
  (import.meta.env.VITE_PLATFORM_BASE as string | undefined) ??
  'https://platform.cogo.xyz'

/**
 * Supabase access-token supplier. Returns null when no session (anonymous).
 * MCP gateway allows unauthenticated reads; Platform REST returns empty
 * list on null (matches `fetchPresetsViaWorker` semantics + PR #65 gracefully
 * degrades HTTP 401/403 to null at fallback layer).
 */
async function getSupabaseAccessToken(): Promise<string | null> {
  try {
    const { data, error } = await supabase.auth.getSession()
    if (error || !data.session) return null
    return data.session.access_token
  } catch {
    return null
  }
}

export interface KgContextValue {
  /** True once init resolved OR the feature flag is off. */
  ready: boolean
  /** True while the init Promise is in flight. */
  loading: boolean
  /** Non-null when init succeeded. */
  initResult: InitResult | null
  /** Non-null when init failed. */
  error: Error | null
  /** Feature flag snapshot — cheap read for consumers. */
  flagEnabled: boolean
  /** Domain Repository. Non-null once `ready === true` AND flag is on AND init succeeded. */
  repo: HybridRepository | null
}

const defaultValue: KgContextValue = {
  ready: !isKgBrowserEnabled(),
  loading: false,
  initResult: null,
  error: null,
  flagEnabled: isKgBrowserEnabled(),
  repo: null,
}

const KgContext = createContext<KgContextValue>(defaultValue)

export interface KgProviderProps {
  children: ReactNode
}

export function KgProvider({ children }: KgProviderProps) {
  const [state, setState] = useState<KgContextValue>(() => {
    const flag = isKgBrowserEnabled()
    return {
      ready: !flag,
      loading: flag,
      initResult: null,
      error: null,
      flagEnabled: flag,
      repo: null,
    }
  })

  // StrictMode double-effect protection — single Repository instance per mount.
  const repoRef = useRef<HybridRepository | null>(null)

  useEffect(() => {
    if (!state.flagEnabled) return
    let cancelled = false

    if (!repoRef.current) {
      const local = new LocalWasmRepository({
        manifestUrl: getManifestUrl(),
        workerBaseUrl: getWorkerBaseUrl(),
        verbose: import.meta.env.DEV,
        ruleRegistry: new MapBackedRuleRegistry({
          structural: STARTER_SHACL_RULES.structural,
          binding: STARTER_SHACL_RULES.binding,
        }),
      })
      const componentFallback = new McpGatewayFallback({
        url: MCP_GATEWAY_URL,
        getAuthToken: getSupabaseAccessToken,
      })
      const dsFallback = new PlatformRestFallback({
        baseUrl: PLATFORM_BASE,
        getAuthToken: getSupabaseAccessToken,
      })
      repoRef.current = new HybridRepository(local, {
        componentFallback,
        dsFallback,
      })
    }
    const repo = repoRef.current

    repo
      .ready()
      .then(() => {
        if (cancelled) return
        let snapshot: InitResult | null = null
        try {
          const v = repo.versionInfo()
          snapshot = {
            initMs: 0,
            loadedTriples: 0,
            shapeSha: v.shapeSha,
            capabilities: {
              crossOriginIsolated:
                typeof globalThis.crossOriginIsolated === 'boolean'
                  ? globalThis.crossOriginIsolated
                  : false,
              sharedArrayBuffer: typeof SharedArrayBuffer === 'function',
              wasmSimd: false,
              wasmBulkMemory: false,
              chosenPath: 'standard',
            },
          } as InitResult
        } catch {
          snapshot = null
        }
        setState((prev) => ({
          ...prev,
          ready: true,
          loading: false,
          initResult: snapshot,
          error: null,
          repo,
        }))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState((prev) => ({
          ...prev,
          // Graceful degrade — flag on but init failed → still ready (server
          // fallback reachable via the legacy kg-client path).
          ready: true,
          loading: false,
          initResult: null,
          error: err instanceof Error ? err : new Error(String(err)),
          repo: null,
        }))
      })

    return () => {
      cancelled = true
    }
  }, [state.flagEnabled])

  return <KgContext.Provider value={state}>{children}</KgContext.Provider>
}

export function useKg(): KgContextValue {
  return useContext(KgContext)
}
